import {getAccountsActiveLicenses} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {LicenseType} from 'src/openapi/admin/models/LicenseType';
import {useAppDispatch} from 'src/store/hooks';
import {getSeatsRangeTitle} from 'src/utils/license-util';

import {FormattedDate} from '@pb/kit-component-formatters';

import {COLUMNS} from '../../../constants';

type Props = {
    data: Record<string, string>[],
    selectedColumns: string[],
    accountIds: number[],
    nameToIdsMap: Record<string, number>,
    getLicenseTypeByCode: (code: string) => string | undefined,
    apiDispatch: ReturnType<typeof useAppDispatch>,
};

export const fetchLicenses = async (props: Props): Promise<Record<string, string>[] | null> => {
    const {data, selectedColumns, accountIds, nameToIdsMap, getLicenseTypeByCode, apiDispatch} = props;
    const licenseColumns = [
        COLUMNS.LICENSE_TYPE,
        COLUMNS.LICENSE_START_DATE,
        COLUMNS.LICENSE_END_DATE,
        COLUMNS.LICENSE_SEATS,
    ];

    if (!licenseColumns.some(col => selectedColumns.includes(col))) {
        return data;
    }

    const response = await apiDispatch(getAccountsActiveLicenses({requestBody: accountIds}));
    if (!response.payload.success) {
        return null;
    }

    const licenses = response.payload.data;

    return data.map(row => {
        const accountId = nameToIdsMap[row[COLUMNS.ACCOUNT_NAME]];
        const license = licenses[accountId];

        return {
            ...row,
            ...(selectedColumns.includes(COLUMNS.LICENSE_TYPE) && {
                [COLUMNS.LICENSE_TYPE]: getLicenseTypeByCode(license?.licenseType) || '',
            }),
            ...(selectedColumns.includes(COLUMNS.LICENSE_START_DATE) && {
                [COLUMNS.LICENSE_START_DATE]: FormattedDate.format({
                    value: license?.licenseStarts,
                    format: 'YYYY-MM-DD',
                }) || '',
            }),
            ...(selectedColumns.includes(COLUMNS.LICENSE_END_DATE) && {
                [COLUMNS.LICENSE_END_DATE]: FormattedDate.format({
                    value: license?.licenseEnds,
                    format: 'YYYY-MM-DD',
                }) || '',
            }),
            ...(selectedColumns.includes(COLUMNS.LICENSE_SEATS) && {
                [COLUMNS.LICENSE_SEATS]: (() => {
                    if (!license?.seatsCount) {
                        return '';
                    }
                    if (license.licenseType === LicenseType.US) {
                        return getSeatsRangeTitle(license.seatsCount).title;
                    }
                    return license.seatsCount.toString();
                })(),
            }),
        };
    });
};
